import React, { useEffect, useRef, useState } from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { Button, NoticeBox, Tags } from "../../ui";
import { getApplicationsQueueAPI } from "../../../utils/api/applications";
import { integrationLabel } from "../../../utils/integrations";
import { errorMessage } from "./applicationsText";
import { REQUEST_TAG, REQUEST_TYPE_TAG } from "./applicationsModel";

// Administrators: subscription requests that still need attention and
// integrations waiting for approval, across every store. "Review" jumps to
// that store's application in the normal management modal, where the
// decision is made (and enforced by the backend).
export default function AdminQueueModal({ open, lang, t, token, onClose, onReview }) {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setState({ status: "loading", data: null, error: "" });
    getApplicationsQueueAPI(token)
      .then((res) => alive.current && setState({ status: "ready", data: res.data, error: "" }))
      .catch((e) => alive.current && setState({ status: "error", data: null, error: errorMessage(t, e) }));
  }, [open, token, t]);

  if (!open) return null;
  const { subscriptions = [], integrations = [], changeRequests = [] } = state.data || {};

  return (
    <LudDashboardModal open title={t.queueModal.title} size="lg" onClose={onClose}>
      {state.status === "loading" && <p className="lud-app-lead" role="status">{t.loading}</p>}
      {state.status === "error" && <NoticeBox variant="danger" size="sm">{state.error}</NoticeBox>}
      {state.status === "ready" && (
        <div className="lud-app-stack">
          <section>
            <h4>{t.changes.title}</h4>
            {changeRequests.length === 0 && <small>{t.queueModal.none}</small>}
            <ul className="lud-app-request-list">
              {changeRequests.map((request) => (
                <li key={request.id} className="lud-app-request">
                  <div className="lud-app-request-head">
                    <strong>{request.storeName}</strong>
                    <span>{integrationLabel(request.app, lang)}</span>
                    <Tags variant={REQUEST_TYPE_TAG[request.type]} appearance="soft" size="sm">{t.requestTypes[request.type]}</Tags>
                  </div>
                  <small dir="ltr">{request.reference} · {t.changes.version(request.version)}</small>
                  {request.reason && <small className="lud-app-reason-text">{`${t.flow.reasonLabel}: ${request.reason}`}</small>}
                  <div className="lud-app-tags">
                    {request.changes.map((change) => <Tags key={change.branchId} variant="neutral" appearance="soft" size="sm">{change.name}</Tags>)}
                  </div>
                  <div className="lud-app-actions">
                    <Button size="sm" variant="primary" appearance="soft" onClick={() => onReview(request.storeId, request.app, request.id)}>{t.queueModal.review}</Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h4>{t.queueModal.subs}</h4>
            {subscriptions.length === 0 && <small>{t.queueModal.none}</small>}
            <ul className="lud-app-request-list">
              {subscriptions.map((request) => (
                <li key={request.id} className="lud-app-request">
                  <div className="lud-app-request-head">
                    <strong>{request.storeName}</strong>
                    <span>{integrationLabel(request.app, lang)}</span>
                    <Tags variant={REQUEST_TAG[request.status] || "neutral"} appearance="soft" size="sm">{t.status[request.status] || request.status}</Tags>
                  </div>
                  <small dir="ltr">{request.reference}</small>
                  <div className="lud-app-tags">
                    {request.branches.map((branch) => <Tags key={branch.branchId} variant="neutral" appearance="soft" size="sm">{branch.name}</Tags>)}
                  </div>
                  <div className="lud-app-actions">
                    <Button size="sm" variant="primary" appearance="soft" onClick={() => onReview(request.storeId, request.app)}>{t.queueModal.review}</Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h4>{t.queueModal.integrations}</h4>
            {integrations.length === 0 && <small>{t.queueModal.none}</small>}
            <ul className="lud-app-request-list">
              {integrations.map((item) => (
                <li key={`${item.storeId}-${item.app}`} className="lud-app-request">
                  <div className="lud-app-request-head">
                    <strong>{item.storeName}</strong>
                    <span>{integrationLabel(item.app, lang)}</span>
                  </div>
                  <div className="lud-app-tags">
                    {item.branches.map((branch) => <Tags key={branch.id} variant="warning" appearance="soft" size="sm">{branch.name}</Tags>)}
                  </div>
                  <div className="lud-app-actions">
                    <Button size="sm" variant="primary" appearance="soft" onClick={() => onReview(item.storeId, item.app)}>{t.queueModal.review}</Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </LudDashboardModal>
  );
}
